import { apiClient, apiEndpoint } from '../../../../../services/api.services';
import type {
  BulkApplyPdfCommentPayload,
  BulkApplyStatusPayload,
  BulkApplyStatusResult,
  BulkCommonSupplier,
  BulkGridPage,
  BulkGridPageQuery,
  ValidateDesignProductsResult,
} from '../types';

/**
 * API client for the Design Details bulk endpoints
 * (POST /api/development-orders/design/bulk/*). The shared `apiClient` response
 * interceptor already unwraps the `{ code, message, data }` envelope, so each
 * call returns the envelope object and the payload lives under `.data`.
 */

const BULK_BASE_URL = `${apiEndpoint}/development-orders/design/bulk`;

interface ResponseEnvelope<T> {
  code?: number;
  message?: string;
  data?: T;
}

/**
 * @description Fetch one page of bulk grid rows (products × canvases). Sorting and
 * searching are server-side: with paging enabled the client only holds the pages
 * loaded so far and cannot order or filter the full result set itself.
 * @param query Selected ids plus the page window, sort direction and search term.
 * @returns The page of rows and the total matching product count.
 */
export const fetchBulkDesignRows = async (query: BulkGridPageQuery): Promise<BulkGridPage> => {
  const response = (await apiClient.post(`${BULK_BASE_URL}/list`, query)) as ResponseEnvelope<BulkGridPage>;
  return response?.data ?? { rows: [], total: 0 };
};

/**
 * @description Split the selection into products that carry design details and those that
 * do not. Called once before the first page load so the grid never lists a product with
 * nothing to edit, and the popup can name the ones it dropped.
 * @param devOrderIds Selected development order ids.
 * @returns Eligible ids plus the excluded products; an empty exclusion list when all qualify.
 */
export const validateDesignProducts = async (devOrderIds: string[]): Promise<ValidateDesignProductsResult> => {
  const response = (await apiClient.post(`${BULK_BASE_URL}/validate`, {
    devOrderIds,
  })) as ResponseEnvelope<ValidateDesignProductsResult>;
  return response?.data ?? { eligibleProductIds: devOrderIds, ineligibleProducts: [] };
};

/**
 * @description Fetch suppliers common to every selected product.
 * @param devOrderIds Selected development order ids.
 * @returns Common suppliers, or an empty array when the intersection is empty.
 */
export const fetchCommonSuppliers = async (devOrderIds: string[]): Promise<BulkCommonSupplier[]> => {
  const response = (await apiClient.post(`${BULK_BASE_URL}/common-suppliers`, {
    devOrderIds,
  })) as ResponseEnvelope<BulkCommonSupplier[]>;
  return response?.data ?? [];
};

/**
 * @description Apply one status/comment/date to the selected canvases across products.
 * @param payload Bulk apply body (items, status, statusComment, commentDate, supplierId).
 * @returns Result summary with the modified canvas count.
 */
export const applyBulkStatus = async (payload: BulkApplyStatusPayload): Promise<BulkApplyStatusResult> => {
  const response = (await apiClient.post(`${BULK_BASE_URL}/add-status`, payload)) as ResponseEnvelope<BulkApplyStatusResult>;
  return response?.data ?? { modifiedCount: 0 };
};

/**
 * @description Apply one PDF-excluded rich-text comment to the selected canvases
 * across products. Writes only the design annotation (`pdfExcludedComment`); the
 * approval status/comment/date are left untouched.
 * @param payload Bulk comment body (items, comment).
 * @returns Result summary with the modified canvas count.
 */
export const applyBulkPdfExcludedComment = async (payload: BulkApplyPdfCommentPayload): Promise<BulkApplyStatusResult> => {
  const response = (await apiClient.post(`${BULK_BASE_URL}/pdf-excluded-comment`, payload)) as ResponseEnvelope<BulkApplyStatusResult>;
  return response?.data ?? { modifiedCount: 0 };
};
